import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { GENERAL_ERROR } from "@/react/components/Paywalls/Checkout/helpers";
import {
  processWithRetries,
  redirectToCheckoutConfirmation,
} from "@/react/components/Paywalls/Checkout/hooks/paymentMethods/helpers";
import { ApiError } from "@/react/config/CustomErrors";
import { usePaywallCheckoutContext } from "@/react/contexts/Paywalls/paywallCheckoutContext";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import {
  isCheckoutAttemptCompleted,
  isCheckoutAttemptProcessingOrWaitingForConfirmation,
} from "@/react/helpers/checkoutAttemptHelpers";
import type { CheckoutAttemptStatus } from "@/react/types/PaywallCheckoutAttempt";
import { paywallCheckoutApi } from "@circle-react/api/paywallCheckoutApi";
import { getQueryParam } from "@circle-react/helpers/urlHelpers";
import { Loader } from "@circle-react-uikit/Loader";

// This only applies right now for Buy Now Pay Later providers where submitting the checkout redirects you to
// the Buy Now Pay Later provider and after confirmation or error you are redirected back to the checkout page

interface CheckoutResponseFailure {
  failure: {
    message?: string;
  };
}

interface CheckoutResponseSuccess {
  checkout_attempt_status: CheckoutAttemptStatus;
}

type CheckoutResponse =
  | null
  | CheckoutResponseFailure
  | CheckoutResponseSuccess;

export const CheckoutRedirectHandler = () => {
  const { checkoutConfirmationUrl, handleError } = usePaywallCheckoutContext();
  const paymentIntent = getQueryParam("payment_intent") || "";
  const paymentMethodType = getQueryParam("payment_method_type") || "";
  const [isPerformingVerification, setIsPerformingVerification] =
    useState<boolean>(false);
  const resetURL = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("payment_intent");
    url.searchParams.delete("payment_intent_client_secret");
    url.searchParams.delete("redirect_status");
    url.searchParams.delete("payment_method_type");
    window.history.replaceState({}, "", url);
  };

  const verifyMutation = useMutation(() =>
    paywallCheckoutApi.verifications.create({
      payment_intent_id: paymentIntent,
      payment_method_type: paymentMethodType,
    }),
  );

  const verifyCheckoutWasProcessed = async (): Promise<any> => {
    try {
      const verifyResponse: CheckoutResponse =
        await verifyMutation.mutateAsync();

      if (!verifyResponse || typeof verifyResponse !== "object") {
        return { failure: { message: GENERAL_ERROR } };
      }

      if ("failure" in verifyResponse) {
        return verifyResponse;
      }

      if ("checkout_attempt_status" in verifyResponse) {
        if (
          isCheckoutAttemptProcessingOrWaitingForConfirmation(
            verifyResponse.checkout_attempt_status,
          )
        ) {
          return null;
        }
        return verifyResponse;
      }
    } catch (e: any) {
      if (e instanceof ApiError) {
        return e.body;
      }
      notifyBugsnag(e);
      return { failure: { message: e.message || GENERAL_ERROR } };
    }
  };

  const handleTerminalFailure = (error_message: string | null) => {
    const message = error_message || GENERAL_ERROR;
    // We delay this to allow the initial Checkout#preview response to load.
    // Otherwise the error message will be cleared and the user won't see it
    setTimeout(() => {
      handleError({
        message,
        disablePayButton: false,
      });
      setIsPerformingVerification(false);
      resetURL();
    }, 2000);
  };
  const performVerification = async () => {
    if (!paymentIntent) return;

    setIsPerformingVerification(true);

    try {
      const checkoutResponse = await processWithRetries({
        executor: verifyCheckoutWasProcessed,
      });

      handleCheckoutResponse(checkoutResponse);
    } catch (error) {
      handleTerminalFailure(null);
    }
  };

  const handleCheckoutResponse = (checkoutResponse: CheckoutResponse) => {
    if (checkoutResponse === null) {
      handleVerificationFailed();
      return;
    }

    if ("failure" in checkoutResponse) {
      const errorMessage = checkoutResponse?.failure?.message || null;
      handleTerminalFailure(errorMessage);
      return;
    }

    if (isCompletedCheckout(checkoutResponse)) {
      redirectToCheckoutConfirmation(checkoutResponse, checkoutConfirmationUrl);
      return;
    }

    // Handle unexpected response format
    handleTerminalFailure(null);
  };

  const isCompletedCheckout = (
    response: CheckoutResponseSuccess | CheckoutResponseFailure,
  ) =>
    "checkout_attempt_status" in response &&
    isCheckoutAttemptCompleted(response.checkout_attempt_status);

  const handleVerificationFailed = () => {
    handleError({
      message: GENERAL_ERROR,
      disablePayButton: false,
    });
    setIsPerformingVerification(false);
    resetURL();
  };

  useEffect(() => {
    void performVerification();
  }, [paymentIntent]);

  if (isPerformingVerification) {
    return (
      <Loader
        variant="fullscreen"
        message={t("paywalls.checkout.bnpl_processing")}
      />
    );
  }

  return null;
};
